import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import './ContactUs.css'

// EmailJS Configuration - Set these in your .env file:
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;


const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setSubmitStatus(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Check if EmailJS is configured
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.error('EmailJS is not configured. Please set environment variables.');
      setSubmitStatus({
        success: false,
        message: 'Email service is not configured. Please contact us directly at contact@pilothelper.com'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare template parameters
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        reason: formData.reason,
        message: formData.message,
        to_email: 'contact@pilothelper.com'
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus({
        success: true,
        message: 'Your message has been sent successfully! We will get back to you soon.'
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        reason: '',
        message: '',
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setSubmitStatus({
        success: false,
        message: 'Failed to send message. Please try again or email us directly at contact@pilothelper.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container contact-form">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          {/* Header */}
          <h1 className="display-4 mb-4">Contact Us</h1>

          {/* Description */}
          <p className="lead mb-5">
            We're here to answer your questions, provide support, and help you become part of the <strong>PilotHelper</strong> movement.
            Whether you're looking to earn your <strong>Peer Support Certificate</strong>, collaborate with us, or simply learn more,
            we'd love to hear from you.
          </p>

          <form onSubmit={handleSubmit} className="mb-5">
            {/* Full Name - Fixed responsive classes */}
            <div className="row mb-3">
              <div className="col-12 col-md-6 mb-3 mb-md-0">
                <label htmlFor="firstName" className="form-label">First Name*</label>
                <input
                  type="text"
                  className="form-control w-100"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  style={{minWidth: '0'}}
                />
              </div>
              <div className="col-12 col-md-6">
                <label htmlFor="lastName" className="form-label">Last Name*</label>
                <input
                  type="text"
                  className="form-control w-100"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  style={{minWidth: '0'}}
                />
              </div>
            </div>

            {/* Email */}
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email Address*</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Phone */}
            <div className="mb-3">
              <label htmlFor="phone" className="form-label">Phone Number (optional)</label>
              <input
                type="tel"
                className="form-control"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            {/* Reason for Contact */}
            <div className="mb-3">
              <label htmlFor="reason" className="form-label">Reason for Contact*</label>
              <select
                className="form-select"
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                required
              >
                <option value="">Please select</option>
                <option value="General Inquiry">General Inquiry</option>
                <option value="Peer Support Certificate">Peer Support Certificate</option>
                <option value="Collaboration">Collaboration</option>
                <option value="Partner">Partner</option>
                <option value="Technical Support">Technical Support</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div className="mb-4">
              <label htmlFor="message" className="form-label">Message*</label>
              <textarea
                className="form-control"
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
              />
            </div>

            {/* Status Message */}
            {submitStatus && (
              <div className={`alert ${submitStatus.success ? 'alert-success' : 'alert-danger'} mb-3`} role="alert">
                {submitStatus.message}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary px-4 py-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </form>

          {/* Contact Email Box */}
          <div className="card shadow-sm mb-5">
            <div className="card-body text-center">
              <h3 className="card-title mb-3">Email us</h3>
              <div>
                <strong>GENERAL INFORMATION</strong>
                <p className="text-danger mt-1 mb-0">
                  <a href='mailto:contact@pilothelper.com' className='fw-bold text-decoration-none' style={{color: 'var(--light-blue)'}}>
                    contact@pilothelper.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;